// import React, { useState } from "react";
// import { useSelector } from "react-redux";
// import { motion, AnimatePresence } from "framer-motion";
// import { AiOutlineDown } from "react-icons/ai";
// import CustomHookForRuralDetails from "../ruralDetailsCount/CustomHookForRuralDetails";
// import DynamicIcon from "../Form/FormFields/DynamicIcon";
// import DynamicLoader from "../Form/FormFields/DynamicLoader";

// interface AccordionProps {
//   title?: string;
//   defaultOpen?: boolean;
//   componentID?: string | number;
// }

// const Accordion: React.FC<AccordionProps> = ({
//   title = "AI Insights",
//   defaultOpen = false,
//   componentID,
// }) => {
//   const selectorData = useSelector((state: any) => state?.dynamicData);
//   const aiData = selectorData?.analysis?.[componentID] || { insights: [] };
//   const componentData = selectorData[componentID] || [];

//   const isLoading =
//     selectorData.analysisLoader?.[`report/chart/${componentID}/analysis`];

//   const { getAnalysisData } = CustomHookForRuralDetails();
//   const [open, setOpen] = useState(defaultOpen);
//   const [hasFetched, setHasFetched] = useState(defaultOpen);

//   const handleToggle = async (id: string | number) => {
//     if (!open && !hasFetched) {
//       setOpen(true); // Open immediately to show loader
//       await getAnalysisData(id, componentData); // Trigger Redux loader
//       setHasFetched(true); // Prevent future re-fetch
//     } else {
//       setOpen((prev) => !prev); // Toggle open/close
//     }
//   };

//   return (
//     <div className="bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 dark:from-slate-800 dark:via-slate-700 dark:to-slate-600">
//       <button
//         onClick={() => handleToggle(componentID)}
//         className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-300"
//       >
//         <div className="flex items-center gap-3">
//           <DynamicIcon
//             name={"Sparkles"}
//             className="h-6 w-6 mb-1 text-blue-500"
//           />
//           <span className="text-base font-semibold text-gray-800 dark:text-gray-100">
//             {title}
//           </span>
//         </div>

//         <motion.div
//           animate={{ rotate: open ? 180 : 0 }}
//           transition={{ duration: 0.3 }}
//         >
//           <AiOutlineDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
//         </motion.div>
//       </button>

//       <AnimatePresence initial={false}>
//         {open && (
//           <motion.div
//             key="content"
//             initial="collapsed"
//             animate="open"
//             exit="collapsed"
//             variants={{
//               open: { height: "auto", opacity: 1 },
//               collapsed: { height: 0, opacity: 0 },
//             }}
//             transition={{ duration: 0.4, ease: "easeInOut" }}
//             className="overflow-hidden px-6"
//           >
//             {isLoading ? (
//               <div className="py-4">
//                 <DynamicLoader loading={true} />
//               </div>
//             ) : (
//               <ul className="py-4 list-disc list-inside space-y-3 text-gray-700 dark:text-gray-300 text-sm">
//                 {aiData?.insights.map((insight: string, idx: number) => (
//                   <motion.li
//                     key={idx}
//                     dangerouslySetInnerHTML={{ __html: insight }}
//                     className="px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors duration-200 [&_b]:font-bold [&_b]:text-blue-700"
//                     initial={{ opacity: 0, y: 10 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ delay: idx * 0.05 }}
//                   />
//                 ))}
//               </ul>
//             )}
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// };

// export default Accordion;

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { AiOutlineDown } from "react-icons/ai";
import CustomHookForRuralDetails from "../ruralDetailsCount/CustomHookForRuralDetails";
import DynamicIcon from "../Form/FormFields/DynamicIcon";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import AiLoader from "../Form/FormFields/AiLoader";
import TypingText from "./TypingText";
import CustomModal from "./CustomModal";
import TextInputWithValidation from "./Form";

interface AccordionProps {
  title?: string;
  defaultOpen?: boolean;
  componentID?: string | number;
  chartData?: any;
  chartValue?: any;
}

const Accordion: React.FC<AccordionProps> = ({
  title = "AI Insights",
  defaultOpen = false,
  chartData,
  chartValue,
  componentID,
}) => {
  const chartFilterData = useSelector(
    (state: any) => state?.dynamicData?.chartFilterData
  );
  const selectorData = useSelector((state: any) => state?.dynamicData);
  const isLoading = componentID
    ? selectorData?.analysisLoader?.[`report/chart/${componentID}/analysis`]
    : false;
  const aiData = (componentID && selectorData?.analysis?.[componentID]) || {
    insights: [],
  };
  const componentData = (componentID && selectorData[componentID]) || [];
  const insights = [
    "Detected <b>high bounce rate</b> on your pricing page.",
    "<b>43%</b> of users drop off after viewing the pricing page.",
    "Recommendation: A/B test a simpler pricing table.",
    "Estimated impact: <b>+12%</b> conversion lift.",
  ];

  const { getAnalysisData } = CustomHookForRuralDetails();
  const [open, setOpen] = useState(defaultOpen);
  const [hasFetched, setHasFetched] = useState(defaultOpen);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleToggle = async (id: string | number) => {
    if (!open && !hasFetched) {
      setOpen(true);
      await getAnalysisData(id, componentData).then(() => {
        setHasFetched(true);
      });
    } else {
      setOpen((prev) => !prev);
    }
  };

  return (
    <div className="bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 dark:from-slate-800 dark:via-slate-700 dark:to-slate-600">
      <button
        onClick={() => componentID && handleToggle(componentID)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-300"
      >
        <div className="flex items-center gap-3">
          <DynamicIcon
            name={"Sparkles"}
            className="h-6 w-6 mb-1 text-blue-500"
          />
          <span className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {title}
          </span>
        </div>

        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <AiOutlineDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { height: "auto", opacity: 1 },
              collapsed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="overflow-hidden px-6"
          >
            {!hasFetched || isLoading ? (
              <div className="py-4 flex flex-col items-start w-full">
                <AiLoader loading={true} />
              </div>
            ) : (
              aiData?.insights?.length > 0 && (
                <TypingText
                  textArray={aiData?.insights}
                  speed={10}
                  componentID={componentID}
                  onAllComplete={() => console.log("User finished reading!")}
                  chartData={chartData}
                  chartValue={chartValue}
                  chartFilter={chartFilterData}
                />
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <CustomModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="Chart Detail"
        size="md"
      >
        <div>
          {isLoading ? (
            <DynamicLoader loading={isLoading} />
          ) : (
            <TextInputWithValidation />
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default Accordion;
